import { sanitizeColumnName, RESERVED_COLUMNS } from './columnName';

// A real column named like the canonical combined address (but NOT a part such
// as address1/addr_2) — detected address parts fold into it instead of spawning
// a separate "address" new column.
const ADDRESS_COLUMN_RE = /^(full_|mailing_|home_|patient_)?address$/;

// A per-column mapping is a combine spec: which source header(s) feed the column
// and how they join. One list of real table columns replaces the old
// "known fields + extra columns" split — every column combines the same way.

export const emptySpec = () => ({ sources: [], separator: ', ' });
export const specFromIndex = (i) => ({ sources: [i], separator: ', ' });

export function isSpecMapped(spec) {
  return !!spec && Array.isArray(spec.sources) && spec.sources.some((i) => i != null);
}

// Immutable edits used by the combine UI (a "+" adds a null slot; picking
// "— none —" clears a slot to null; the last real source can still be cleared).
export const addSource = (spec) => ({ ...emptySpec(), ...spec, sources: [...(spec ? spec.sources : []), null] });
export const removeSource = (spec, slot) => ({ ...spec, sources: spec.sources.filter((_, i) => i !== slot) });
// Set the source at `slot`, padding with nulls if the slot is at/beyond the
// current length. This matters because an unmapped column renders one empty slot
// even though its spec has an empty sources array — picking a value there must
// create sources[0] rather than map over nothing and drop the selection.
export const setSource = (spec, slot, value) => {
  const v = value === '' || value == null ? null : Number(value);
  const sources = (spec && spec.sources ? spec.sources : []).slice();
  while (sources.length <= slot) sources.push(null);
  sources[slot] = v;
  return { ...emptySpec(), ...spec, sources };
};
export const setSeparator = (spec, separator) => ({ ...emptySpec(), ...spec, separator });

// Build the inputs transformRows/commitImport expect from the unified state:
//  - columnMap: fieldKey -> combine spec (only for columns that route to a
//    special parser, e.g. phone_numbers -> tel_num);
//  - extraColumns: [{name, sources, separator}] for every other mapped column
//    (plain real columns AND user-created new columns), name sanitized;
//  - newColumnNames: the subset that doesn't already exist and must be created.
// `columnCombine` is keyed by real column name; `newColumns` are user-added.
export function deriveMappingInputs({ displayColumns, columnCombine = {}, newColumns = [], existingColumns = null }) {
  const columnMap = {};
  const extraColumns = [];
  const known = existingColumns instanceof Set ? existingColumns : (Array.isArray(existingColumns) ? new Set(existingColumns) : null);
  const newColumnNames = [];

  (displayColumns || []).forEach(({ col, fieldKey }) => {
    const spec = columnCombine[col];
    if (!isSpecMapped(spec)) return;
    if (fieldKey) columnMap[fieldKey] = spec;
    else extraColumns.push({ name: col, sources: spec.sources, separator: spec.separator });
  });

  newColumns.forEach((c) => {
    const name = sanitizeColumnName(c.name);
    if (!name || !isSpecMapped(c)) return;
    extraColumns.push({ name, sources: c.sources, separator: c.separator });
    if (!known || !known.has(name)) newColumnNames.push(name);
  });

  return { columnMap, extraColumns, newColumnNames };
}

// Build the initial mapping state from the file's headers + auto-match, mirroring
// the old seedExtraColumns behaviour under the unified model:
//  - columnCombine: each real column's combine spec (special columns from the
//    auto-match, name folds in split parts, an existing "address" column soaks up
//    detected address parts, plain columns auto-match a same-named header);
//  - newColumns: auto-suggested columns for every header NOT already consumed by a
//    real column — so unmapped headers (e.g. "addr_1") surface at the bottom, and
//    >=2 detected address parts collapse into one combined "address" (unless the
//    table already has an address column, which absorbs them above).
// `columnMap` is matchColumns' fieldKey->index map; `nameParts`/`addressPartIdx`
// are the header indices for the split-name and address heuristics. Auto-suggested
// entries get `auto_*` ids so a caller can preserve user-added (`nc_*`) columns.
export function buildSeed({
  displayColumns = [], headers = [], columnMap = {}, nameParts = [], addressPartIdx = [], existingColumns = null,
} = {}) {
  const known = existingColumns instanceof Set ? existingColumns : (Array.isArray(existingColumns) ? new Set(existingColumns) : null);
  const hasAddressCombo = addressPartIdx.filter((i) => i != null).length >= 2;
  const addrIdx = addressPartIdx.filter((i) => i != null);

  const columnCombine = {};
  const used = new Set();
  let addressConsumed = false;

  displayColumns.forEach(({ col, fieldKey }) => {
    if (fieldKey === 'patient_name' && columnMap.patient_name == null) {
      columnCombine[col] = nameParts.length ? { sources: [...nameParts], separator: ' ' } : emptySpec();
    } else if (fieldKey) {
      let idx = columnMap[fieldKey];
      // A target with ONE phone column (the roster's phone_numbers) takes the
      // home number when the file has no cell — home and cell used to share
      // tel_num's synonyms, and a home-only sheet must not lose its phone now.
      if (idx == null && fieldKey === 'tel_num' && !displayColumns.some((d) => d.fieldKey === 'home_phone')) {
        idx = columnMap.home_phone;
      }
      columnCombine[col] = idx == null ? emptySpec() : specFromIndex(idx);
    } else if (hasAddressCombo && ADDRESS_COLUMN_RE.test(col)) {
      columnCombine[col] = { sources: [...addrIdx], separator: ', ' };
      addressConsumed = true;
    } else {
      const idx = headers.findIndex((h) => sanitizeColumnName(h) === col);
      columnCombine[col] = idx >= 0 ? specFromIndex(idx) : emptySpec();
    }
  });

  Object.values(columnCombine).forEach((spec) => (spec.sources || []).forEach((i) => { if (i != null) used.add(i); }));

  const newColumns = [];
  const seenNames = new Set();
  if (hasAddressCombo && !addressConsumed && !(known && known.has('address'))) {
    newColumns.push({ id: 'auto_address', name: 'address', sources: [...addrIdx], separator: ', ' });
    seenNames.add('address');
    addrIdx.forEach((i) => used.add(i));
  }
  headers.forEach((h, i) => {
    if (used.has(i)) return;
    const name = sanitizeColumnName(h);
    if (!name || RESERVED_COLUMNS.has(name) || seenNames.has(name)) return;
    if (known && known.has(name)) return; // an existing real column already covers it
    seenNames.add(name);
    newColumns.push({ id: `auto_${i}`, name, sources: [i], separator: ', ' });
  });

  return { columnCombine, newColumns };
}
